/* eslint-disable max-lines -- just text page*/
import React from 'react';
import { Link } from '@tanstack/react-router';

import AuthFooter from '@/components/molecules/AuthFooter/AuthFooter';
import AuthHeader from '@/components/molecules/AuthHeader/AuthHeader';

const tableOfContents = [
  { id: 'our-services', title: 'Our Services' },
  { id: 'intellectual-property-rights', title: 'Intellectual Property Rights' },
  { id: 'user-representations', title: 'User Representations' },
  { id: 'user-registration', title: 'User Registration' },
  { id: 'purchases-and-payment', title: 'Purchases and Payment' },
  { id: 'subscriptions', title: 'Subscriptions' },
  { id: 'prohibited-activities', title: 'Prohibited Activities' },
  { id: 'user-generated-contributions', title: 'User-Generated Contributions' },
  { id: 'contribution-license', title: 'Contribution License' },
  { id: 'mobile-application-license', title: 'Mobile Application License' },
  { id: 'web-application-license', title: 'Web Application License' },
  { id: 'services-management', title: 'Services Management' },
  { id: 'privacy-policy', title: 'Privacy Policy' },
  { id: 'term-and-termination', title: 'Term and Termination' },
  { id: 'modifications-and-interruptions', title: 'Modifications and Interruptions' },
  { id: 'governing-law', title: 'Governing Law' },
  { id: 'dispute-resolution', title: 'Dispute Resolution' },
  { id: 'corrections', title: 'Corrections' },
  { id: 'disclaimer', title: 'Disclaimer' },
  { id: 'limitations-of-liability', title: 'Limitations of Liability' },
  { id: 'indemnification', title: 'Indemnification' },
  { id: 'user-data', title: 'User Data' },
  { id: 'electronic-communications', title: 'Electronic Communications, Transactions, and Signatures' },
  { id: 'california-users', title: 'California Users and Residents' },
  { id: 'miscellaneous', title: 'Miscellaneous' },
  { id: 'contact-us', title: 'Contact Us' },
] as const;

const prohibitedActivities = [
  'Systematically retrieve data or other content from the Services to create or compile, directly or indirectly, a collection, compilation, database, or directory without written permission from us.',
  'Trick, defraud, or mislead us and other users, especially in any attempt to learn sensitive account information such as user passwords.',
  'Circumvent, disable, or otherwise interfere with security-related features of the Services, including features that prevent or restrict the use or copying of any Content or enforce limitations on the use of the Services and/or the Content contained therein.',
  'Disparage, tarnish, or otherwise harm, in our opinion, us and/or the Services.',
  'Use any information obtained from the Services in order to harass, abuse, or harm another person.',
  'Make improper use of our support services or submit false reports of abuse or misconduct.',
  'Use the Services in a manner inconsistent with any applicable laws or regulations.',
  'Engage in unauthorized framing of or linking to the Services.',
  'Upload or transmit, or attempt to upload or transmit, viruses, Trojan horses, or other material, including excessive use of capital letters and spamming, that interferes with any party\'s uninterrupted use and enjoyment of the Services or modifies, impairs, disrupts, alters, or interferes with the use, features, functions, operation, or maintenance of the Services.',
  'Engage in any automated use of the system, such as using scripts to send comments or messages, or using any data mining, robots, or similar data gathering and extraction tools.',
  'Delete the copyright or other proprietary rights notice from any Content.',
  'Attempt to impersonate another user or person or use the username of another user.',
  'Upload or transmit, or attempt to upload or transmit, any material that acts as a passive or active information collection or transmission mechanism, including without limitation clear graphics interchange formats, 1x1 pixels, web bugs, cookies, or other similar devices.',
  'Interfere with, disrupt, or create an undue burden on the Services or the networks or services connected to the Services.',
  'Harass, annoy, intimidate, or threaten any of our employees or agents engaged in providing any portion of the Services to you.',
  'Attempt to bypass any measures of the Services designed to prevent or restrict access to the Services, or any portion of the Services.',
  'Copy or adapt the Services\' software, including but not limited to Flash, PHP, HTML, JavaScript, or other code.',
  'Except as permitted by applicable law, decipher, decompile, disassemble, or reverse engineer any of the software comprising or in any way making up a part of the Services.',
  'Except as may be the result of standard search engine or internet browser usage, use, launch, develop, or distribute any automated system, including without limitation any spider, robot, cheat utility, scraper, or offline reader that accesses the Services, or use or launch any unauthorized script or other software.',
  'Use a buying agent or purchasing agent to make purchases on the Services.',
  'Make any unauthorized use of the Services, including collecting usernames and/or email addresses of users by electronic or other means for the purpose of sending unsolicited email, or creating user accounts by automated means or under false pretenses.',
  'Use the Services as part of any effort to compete with us or otherwise use the Services and/or the Content for any revenue-generating endeavor or commercial enterprise.',
  'Use the Services to advertise or offer to sell goods and services.',
  'Sell or otherwise transfer your profile.',
] as const;

const paymentMethods = ['Visa', 'Mastercard', 'PayPal', 'ACH/eCheck'] as const;

const heading1Classes = 'mb-8 text-[clamp(2rem,4vw,2.75rem)] font-bold leading-tight tracking-[-0.03em] text-foreground';
const heading2Classes = 'mb-3 text-xl font-bold leading-snug text-foreground';
const heading3Classes = 'mb-2 text-lg font-semibold leading-snug text-foreground';
const paragraphClasses = 'mb-4 text-base leading-7 text-foreground';
const listClasses = 'mb-4 ml-6 list-decimal space-y-2 text-base leading-7 text-foreground';

const TermsOfServicePage: React.FC = () => (
  <div className="min-h-screen bg-background text-foreground">
    <AuthHeader />

    <main className="px-6 py-12 lg:px-12">
      <div className="mx-auto w-full max-w-4xl rounded-3xl border border-border bg-card px-6 py-8 shadow-elegant sm:px-8 lg:px-10">
        <h1 className={heading1Classes}>
          Terms of Service
          <br />
          <span className="text-[clamp(0.95rem,2vw,1.125rem)] font-medium tracking-normal text-muted-foreground">
            Last Updated On 04-Feb-2026
          </span>
        </h1>

        <section id="agreement-to-our-legal-terms" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Agreement to Our Legal Terms</h2>
          <p className={paragraphClasses}>
            We are SIX3RO, Inc. ("Company," "we," "us," "our"), a company located in Virginia, United
            States at 11350 Random Hills Road, Suite 841, Fairfax, VA 22030.
          </p>
          <p className={paragraphClasses}>
            We operate the website http://www.six3ro.com (the "Site"), the web and mobile application
            versions of PortalSIX (the "App"), as well as any other related products and services that refer
            or link to these legal terms (the "Legal Terms") (collectively, the "Services").
          </p>
          <p className={paragraphClasses}>
            You can contact us by phone at +1 (703) 721-8202, email at info@six3ro.com, or by mail to 11350
            Random Hills Road, Suite 841, Fairfax, VA 22030, United States.
          </p>
          <p className={paragraphClasses}>
            These Legal Terms constitute a legally binding agreement made between you, whether personally or
            on behalf of an entity ("you"), and SIX3RO, Inc., concerning your access to and use of the
            Services. You agree that by accessing the Services, you have read, understood, and agreed to be
            bound by all of these Legal Terms. If you do not agree with all of these Legal Terms, then you are
            expressly prohibited from using the Services and you must discontinue use immediately.
          </p>
          <p className={paragraphClasses}>
            Supplemental terms and conditions or documents that may be posted on the Services from time to time
            are hereby expressly incorporated herein by reference. We reserve the right, in our sole
            discretion, to make changes or modifications to these Legal Terms from time to time. We will alert
            you about any changes by updating the "Last updated" date of these Legal Terms, and you waive any
            right to receive specific notice of each such change. It is your responsibility to periodically
            review these Legal Terms to stay informed of updates. You will be subject to and will be deemed to
            have been made aware of and to have accepted the changes in any revised Legal Terms by your
            continued use of the Services after the date such revised Legal Terms are posted.
          </p>
          <p className={paragraphClasses}>
            The Services are intended for users who are at least 18 years old. Persons under the age of 18 are
            not permitted to use or register for the Services. We recommend that you print a copy of these
            Legal Terms for your records.
          </p>
        </section>

        <section id="table-of-contents" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Table of Contents</h2>
          <ol className={listClasses}>
            {tableOfContents.map((section) => (
              <li key={section.id}>
                <a className="text-accent underline underline-offset-2" href={`#${section.id}`}>
                  {section.title}
                </a>
              </li>
            ))}
          </ol>
        </section>

        <section id="our-services" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Our Services</h2>
          <p className={paragraphClasses}>
            The information provided when using the Services is not intended for distribution to or use by any
            person or entity in any jurisdiction or country where such distribution or use would be contrary to
            law or regulation or which would subject us to any registration requirement within such
            jurisdiction or country. Accordingly, those persons who choose to access the Services from other
            locations do so on their own initiative and are solely responsible for compliance with local laws,
            if and to the extent local laws are applicable.
          </p>
          <p className={paragraphClasses}>
            While the Services may be inherently compliant with, they are not tailored to comply with
            industry-specific regulations, including HIPAA and FISMA. If your interactions would be subjected
            to such laws, you may not use the Services. You may not use the Services in a way that would
            violate the Gramm-Leach-Bliley Act (GLBA).
          </p>
        </section>

        <section id="intellectual-property-rights" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Intellectual Property Rights</h2>
          <h3 className={heading3Classes}>Our intellectual property</h3>
          <p className={paragraphClasses}>
            We are the owner or the licensee of all intellectual property rights in our Services, including
            all source code, databases, functionality, software, website designs, audio, video, text,
            photographs, and graphics in the Services (collectively, the "Content"), as well as the
            trademarks, service marks, and logos contained therein (the "Marks").
          </p>
          <p className={paragraphClasses}>
            Our Content and Marks are protected by copyright and trademark laws, other intellectual property
            rights, unfair competition laws, and treaties in the United States and around the world.
          </p>
          <p className={paragraphClasses}>
            The Content and Marks are provided in or through the Services "as is" for your personal,
            non-commercial use or internal business purpose only.
          </p>

          <h3 className={heading3Classes}>Your use of our Services</h3>
          <p className={paragraphClasses}>
            Subject to your compliance with these Legal Terms, including the prohibited activities section
            below, we grant you a non-exclusive, non-transferable, revocable license to access the Services
            and download or print a copy of any portion of the Content to which you have properly gained
            access, solely for your personal, non-commercial use or internal business purpose.
          </p>
          <p className={paragraphClasses}>
            Except as set out in this section or elsewhere in our Legal Terms, no part of the Services and no
            Content or Marks may be copied, reproduced, aggregated, republished, uploaded, posted, publicly
            displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise exploited
            for any commercial purpose whatsoever, without our express prior written permission.
          </p>
          <p className={paragraphClasses}>
            If you wish to make any use of the Services, Content, or Marks other than as set out in this
            section or elsewhere in our Legal Terms, please address your request to info@six3ro.com. If we ever
            grant you permission to post, reproduce, or publicly display any part of our Services or Content,
            you must identify us as the owners or licensors of the Services, Content, or Marks and ensure that
            any copyright or proprietary notice appears or is visible on posting, reproducing, or displaying
            our Content.
          </p>
          <p className={paragraphClasses}>
            We reserve all rights not expressly granted to you in and to the Services, Content, and Marks.
            Any breach of these intellectual property rights will constitute a material breach of our Legal
            Terms and your right to use our Services will terminate immediately.
          </p>

          <h3 className={heading3Classes}>Your submissions</h3>
          <p className={paragraphClasses}>
            Please review this section and the prohibited activities section carefully prior to using our
            Services to understand the rights you give us and the obligations you have when you post or upload
            any content through the Services.
          </p>
          <p className={paragraphClasses}>
            By directly sending us any question, comment, suggestion, idea, feedback, or other information
            about the Services ("Submissions"), you agree to assign to us all intellectual property rights in
            such Submission. You agree that we shall own this Submission and be entitled to its unrestricted
            use and dissemination for any lawful purpose, commercial or otherwise, without acknowledgment or
            compensation to you.
          </p>
          <p className={paragraphClasses}>
            By sending us Submissions through any part of the Services, you confirm that you have read and
            agree with our prohibited activities, will not submit unlawful or misleading material, waive moral
            rights to the extent permissible by law, warrant that your Submissions are original or properly
            licensed, and represent that they do not constitute confidential information.
          </p>
          <p className={paragraphClasses}>
            You are solely responsible for your Submissions and expressly agree to reimburse us for losses we
            may suffer because of your breach of these Legal Terms, any third party\'s intellectual property
            rights, or applicable law. Submissions do not include or contemplate any information you transfer
            using the PortalSIX secure information exchange capability. We do not own or have access to such
            material.
          </p>
        </section>

        <section id="user-representations" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>User Representations</h2>
          <p className={paragraphClasses}>
            By using the Services, you represent and warrant that: (1) all registration information you submit
            will be true, accurate, current, and complete; (2) you will maintain the accuracy of such
            information and promptly update such registration information as necessary; (3) you have the legal
            capacity and you agree to comply with these Legal Terms; (4) you are not a minor in the
            jurisdiction in which you reside; (5) you will not access the Services through automated or
            non-human means, whether through a bot, script, or otherwise; (6) you will not use the Services
            for any illegal or unauthorized purpose; and (7) your use of the Services will not violate any
            applicable law or regulation.
          </p>
          <p className={paragraphClasses}>
            If you provide any information that is untrue, inaccurate, not current, or incomplete, we have the
            right to suspend or terminate your account and refuse any and all current or future use of the
            Services or any portion thereof.
          </p>
        </section>

        <section id="user-registration" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>User Registration</h2>
          <p className={paragraphClasses}>
            You may be required to register to use the Services. You agree to keep your password confidential
            and will be responsible for all use of your account and password. We reserve the right to remove,
            reclaim, or change a username you select if we determine, in our sole discretion, that such
            username is inappropriate, obscene, or otherwise objectionable.
          </p>
        </section>

        <section id="purchases-and-payment" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Purchases and Payment</h2>
          <p className={paragraphClasses}>We accept the following forms of payment:</p>
          <ul className="mb-4 ml-6 list-disc space-y-2 text-base leading-7 text-foreground">
            {paymentMethods.map((method) => (
              <li key={method}>{method}</li>
            ))}
          </ul>
          <p className={paragraphClasses}>
            You agree to provide current, complete, and accurate purchase and account information for all
            purchases made via the Services. You further agree to promptly update account and payment
            information, including email address, payment method, and payment card expiration date, so that we
            can complete your transactions and contact you as needed. Sales tax will be added to the price of
            purchases as deemed required by us. We may change prices at any time. All payments shall be in US
            dollars.
          </p>
          <p className={paragraphClasses}>
            You agree to pay all charges at the prices then in effect for your purchases and any applicable
            shipping fees, and you authorize us to charge your chosen payment provider for any such amounts
            upon placing your order. We reserve the right to correct any errors or mistakes in pricing, even if
            we have already requested or received payment.
          </p>
          <p className={paragraphClasses}>
            We reserve the right to refuse any order placed through the Services. We may, in our sole
            discretion, limit or cancel quantities purchased per person, per household, or per order. These
            restrictions may include orders placed by or under the same customer account, the same payment
            method, and/or orders that use the same billing or shipping address. We reserve the right to limit
            or prohibit orders that, in our sole judgment, appear to be placed by dealers, resellers, or
            distributors.
          </p>
        </section>

        <section id="subscriptions" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Subscriptions</h2>
          <h3 className={heading3Classes}>Billing and Renewal</h3>
          <p className={paragraphClasses}>
            Your subscription will continue and automatically renew unless canceled. You consent to our
            charging your payment method on a recurring basis without requiring your prior approval for each
            recurring charge, until such time as you cancel the applicable order. The length of your billing
            cycle is annual.
          </p>
          <h3 className={heading3Classes}>Free Trial</h3>
          <p className={paragraphClasses}>
            We offer a 30-day free trial to new users who register with the Services. The account will be
            charged according to the user\'s chosen subscription at the end of the free trial.
          </p>
          <h3 className={heading3Classes}>Cancellation</h3>
          <p className={paragraphClasses}>
            All purchases are non-refundable. You can cancel your subscription at any time by contacting us
            using the contact information provided below. Your cancellation will take effect at the end of the
            current paid term. If you have any questions or are unsatisfied with our Services, please email us
            at info@six3ro.com.
          </p>
          <h3 className={heading3Classes}>Fee Changes</h3>
          <p className={paragraphClasses}>
            We may, from time to time, make changes to the subscription fee and will communicate any price
            changes to you in accordance with applicable law.
          </p>
        </section>

        <section id="prohibited-activities" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Prohibited Activities</h2>
          <p className={paragraphClasses}>
            You may not access or use the Services for any purpose other than that for which we make the
            Services available. The Services may not be used in connection with any commercial endeavors except
            those that are specifically endorsed or approved by us.
          </p>
          <p className={paragraphClasses}>As a user of the Services, you agree not to:</p>
          <ol className={listClasses}>
            {prohibitedActivities.map((activity) => (
              <li key={activity}>{activity}</li>
            ))}
          </ol>
        </section>

        <section id="user-generated-contributions" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>User-Generated Contributions</h2>
          <p className={paragraphClasses}>
            The Services do not offer users to submit or post content. We may provide you with the opportunity
            to create, submit, post, display, transmit, perform, publish, distribute, or broadcast content and
            materials to us or on the Services, including but not limited to text, writings, video, audio,
            photographs, graphics, comments, suggestions, personal information, or other material
            (collectively, "Contributions"). Contributions may be viewable by other users of the Services and
            through third-party websites. As such, any Contributions you transmit may be treated in accordance
            with the Services\' Privacy Policy.
          </p>
          <p className={paragraphClasses}>
            When you create or make available any Contributions, you thereby represent and warrant that the
            creation, distribution, transmission, public display, performance, accessing, downloading, and
            copying of your Contributions do not and will not infringe third-party rights; that you have all
            required licenses, releases, and permissions; that your Contributions are not false, misleading,
            obscene, abusive, unlawful, discriminatory, or otherwise objectionable; and that your Contributions
            do not violate any applicable law, regulation, privacy right, or provision of these Legal Terms.
          </p>
          <p className={paragraphClasses}>
            Any use of the Services in violation of the foregoing violates these Legal Terms and may result in,
            among other things, termination or suspension of your rights to use the Services.
          </p>
        </section>

        <section id="contribution-license" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Contribution License</h2>
          <p className={paragraphClasses}>
            You and the Services agree that we may access, with the exception of information you exchange using
            the Service\'s secure information exchange capability, store, process, and use any information and
            personal data that you provide following the terms of the Privacy Policy and your choices,
            including settings.
          </p>
          <p className={paragraphClasses}>
            By submitting suggestions or other feedback regarding the Services, you agree that we can use and
            share such feedback for any purpose without compensation to you.
          </p>
          <p className={paragraphClasses}>
            We do not assert any ownership over your Contributions. You retain full ownership of all of your
            Contributions and any intellectual property rights or other proprietary rights associated with your
            Contributions. We are not liable for any statements or representations in your Contributions
            provided by you in any area on the Services. You are solely responsible for your Contributions to
            the Services and you expressly agree to exonerate us from any responsibility and to refrain from
            any legal action against us regarding your Contributions.
          </p>
        </section>

        <section id="mobile-application-license" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Mobile Application License</h2>
          <h3 className={heading3Classes}>Use License</h3>
          <p className={paragraphClasses}>
            If you access the Services via the App, then we grant you a revocable, non-exclusive,
            non-transferable, limited right to install and use the App on wireless electronic devices owned or
            controlled by you, and to access and use the App on such devices strictly in accordance with the
            terms and conditions of this mobile application license contained in these Legal Terms.
          </p>
          <p className={paragraphClasses}>
            You shall not: (1) except as permitted by applicable law, decompile, reverse engineer,
            disassemble, attempt to derive the source code of, or decrypt the App; (2) make any modification,
            adaptation, improvement, enhancement, translation, or derivative work from the App; (3) violate
            any applicable laws, rules, or regulations in connection with your access or use of the App; (4)
            remove, alter, or obscure any proprietary notice posted by us or the licensors of the App; (5) use
            the App for any revenue-generating endeavor, commercial enterprise, or other purpose for which it
            is not designed or intended; (6) make the App available over a network or other environment
            permitting access or use by multiple devices or users at the same time; (7) use the App for
            creating a product, service, or software that is, directly or indirectly, competitive with or in
            any way a substitute for the App; (8) use the App to send automated queries to any website or to
            send any unsolicited commercial email; or (9) use any proprietary information or any of our
            interfaces or our other intellectual property in the design, development, manufacture, licensing,
            or distribution of any applications, accessories, or devices for use with the App.
          </p>
          <h3 className={heading3Classes}>Apple and Android Devices</h3>
          <p className={paragraphClasses}>
            The following terms apply when you use the App obtained from either the Apple Store or Google Play,
            each an "App Distributor," to access the Services: the license granted to you for our App is
            limited to a non-transferable license to use the application on a device that utilizes the Apple
            iOS or Android operating systems, as applicable, and in accordance with the usage rules set forth
            in the applicable App Distributor\'s terms of service; we are responsible for providing any
            maintenance and support services with respect to the App as specified in these Legal Terms or as
            otherwise required under applicable law; and you acknowledge and agree that the App Distributors
            are third-party beneficiaries of this mobile application license and may enforce it against you.
          </p>
        </section>

        <section id="web-application-license" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Web Application License</h2>
          <h3 className={heading3Classes}>Use License</h3>
          <p className={paragraphClasses}>
            If you access the Services via the Web App, then we grant you a revocable, non-exclusive,
            non-transferable, limited right to install and use the Web App on electronic devices owned or
            controlled by you, and to access and use the Web App on such devices strictly in accordance with
            the terms and conditions of this license contained in these Legal Terms.
          </p>
          <p className={paragraphClasses}>
            You shall not: (1) except as permitted by applicable law, decompile, reverse engineer,
            disassemble, attempt to derive the source code of, or decrypt the Web App; (2) make any
            modification, adaptation, improvement, enhancement, translation, or derivative work from the Web
            App; (3) violate any applicable laws, rules, or regulations in connection with your access or use
            of the Web App; (4) remove, alter, or obscure any proprietary notice posted by us or the licensors
            of the Web App; (5) use the Web App for any revenue-generating endeavor, commercial enterprise, or
            other purpose for which it is not designed or intended; (6) make the Web App available over a
            network or other environment permitting access or use by multiple devices or users at the same
            time; (7) use the Web App for creating a product, service, or software that is, directly or
            indirectly, competitive with or in any way a substitute for the Web App; (8) use the Web App to
            send automated queries to any website or to send any unsolicited commercial email; or (9) use any
            proprietary information or any of our interfaces or our other intellectual property in the design,
            development, manufacture, licensing, or distribution of any applications, accessories, or devices
            for use with the Web App.
          </p>
        </section>

        <section id="services-management" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Services Management</h2>
          <p className={paragraphClasses}>
            We reserve the right, but not the obligation, to monitor the Services for violations of these Legal
            Terms; take appropriate legal action against anyone who, in our sole discretion, violates the law
            or these Legal Terms, including reporting such user to law enforcement authorities; refuse,
            restrict access to, limit the availability of, or disable any of your Contributions or any portion
            thereof; remove from the Services or otherwise disable files and content that are excessive in size
            or in any way burdensome to our systems; and otherwise manage the Services in a manner designed to
            protect our rights and property and to facilitate the proper functioning of the Services.
          </p>
        </section>

        <section id="privacy-policy" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Privacy Policy</h2>
          <p className={paragraphClasses}>
            We care about data privacy and security. Please review our{' '}
            <Link to="/privacy" className="text-accent underline underline-offset-2">
              Privacy Policy
            </Link>
            . By using the Services, you agree to be bound by our Privacy Policy, which is incorporated into
            these Legal Terms. Please be advised the Services are hosted in the United States. If you access
            the Services from any other region of the world with laws or other requirements governing personal
            data collection, use, or disclosure that differ from applicable laws in the United States, then
            through your continued use of the Services, you are transferring your data to the United States,
            and you expressly consent to have your data transferred to and processed in the United States.
          </p>
        </section>

        <section id="term-and-termination" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Term and Termination</h2>
          <p className={paragraphClasses}>
            These Legal Terms shall remain in full force and effect while you use the Services. Without
            limiting any other provision of these Legal Terms, we reserve the right to, in our sole discretion
            and without notice or liability, deny access to and use of the Services, including blocking certain
            IP addresses, to any person for any reason or for no reason, including without limitation for
            breach of any representation, warranty, or covenant contained in these Legal Terms or of any
            applicable law or regulation. We may terminate your use or participation in the Services or delete
            your account and any content or information that you posted at any time, without warning, in our
            sole discretion.
          </p>
          <p className={paragraphClasses}>
            If we terminate or suspend your account for any reason, you are prohibited from registering and
            creating a new account under your name, a fake or borrowed name, or the name of any third party,
            even if you may be acting on behalf of the third party. In addition to terminating or suspending
            your account, we reserve the right to take appropriate legal action, including without limitation
            pursuing civil, criminal, and injunctive redress.
          </p>
        </section>

        <section id="modifications-and-interruptions" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Modifications and Interruptions</h2>
          <p className={paragraphClasses}>
            We reserve the right to change, modify, or remove the contents of the Services at any time or for
            any reason at our sole discretion without notice. However, we have no obligation to update any
            information on our Services. We will not be liable to you or any third party for any modification,
            price change, suspension, or discontinuance of the Services.
          </p>
          <p className={paragraphClasses}>
            We cannot guarantee the Services will be available at all times. We may experience hardware,
            software, or other problems or need to perform maintenance related to the Services, resulting in
            interruptions, delays, or errors. We reserve the right to change, revise, update, suspend,
            discontinue, or otherwise modify the Services at any time or for any reason without notice to you.
            You agree that we have no liability whatsoever for any loss, damage, or inconvenience caused by
            your inability to access or use the Services during any downtime or discontinuance of the Services.
            Nothing in these Legal Terms will be construed to obligate us to maintain and support the Services
            or to supply any corrections, updates, or releases in connection therewith.
          </p>
        </section>

        <section id="governing-law" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Governing Law</h2>
          <p className={paragraphClasses}>
            These Legal Terms and your use of the Services are governed by and construed in accordance with the
            laws of the Commonwealth of Virginia applicable to agreements made and to be entirely performed
            within the Commonwealth of Virginia, without regard to its conflict of law principles.
          </p>
        </section>

        <section id="dispute-resolution" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Dispute Resolution</h2>
          <h3 className={heading3Classes}>Informal Negotiations</h3>
          <p className={paragraphClasses}>
            To expedite resolution and control the cost of any dispute, controversy, or claim related to these
            Legal Terms, the parties agree to first attempt to negotiate any dispute, except those expressly
            provided below, informally for at least thirty days before initiating arbitration. Such informal
            negotiations commence upon written notice from one party to the other party.
          </p>
          <h3 className={heading3Classes}>Binding Arbitration</h3>
          <p className={paragraphClasses}>
            If the parties are unable to resolve a dispute through informal negotiations, the dispute, except
            those expressly excluded below, will be finally and exclusively resolved by binding arbitration.
            You understand that without this provision, you would have the right to sue in court and have a
            jury trial. The arbitration shall be commenced and conducted under the Commercial Arbitration Rules
            of the American Arbitration Association ("AAA") and, where appropriate, the AAA\'s Supplementary
            Procedures for Consumer Related Disputes. Arbitration may be conducted in person, through the
            submission of documents, by phone, or online. Except where otherwise required by applicable rules
            or law, the arbitration will take place in Fairfax County, Virginia.
          </p>
          <p className={paragraphClasses}>
            If for any reason a dispute proceeds in court rather than arbitration, the dispute shall be
            commenced or prosecuted in the state and federal courts located in Fairfax County, Virginia, and
            the parties consent to and waive all defenses of lack of personal jurisdiction and forum non
            conveniens with respect to venue and jurisdiction in such courts. In no event shall any dispute
            brought by either party related in any way to the Services be commenced more than one year after
            the cause of action arose.
          </p>
          <h3 className={heading3Classes}>Restrictions</h3>
          <p className={paragraphClasses}>
            The parties agree that any arbitration shall be limited to the dispute between the parties
            individually. To the fullest extent permitted by law, no arbitration shall be joined with any other
            proceeding, there is no right or authority for any dispute to be arbitrated on a class-action
            basis or to utilize class action procedures, and there is no right or authority for any dispute to
            be brought in a purported representative capacity on behalf of the general public or any other
            persons.
          </p>
          <h3 className={heading3Classes}>Exceptions</h3>
          <p className={paragraphClasses}>
            The parties agree that disputes seeking to enforce or protect intellectual property rights, disputes
            related to allegations of theft, piracy, invasion of privacy, or unauthorized use, and claims for
            injunctive relief are not subject to the informal negotiation and arbitration provisions above.
          </p>
        </section>

        <section id="corrections" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Corrections</h2>
          <p className={paragraphClasses}>
            There may be information on the Services that contains typographical errors, inaccuracies, or
            omissions, including descriptions, pricing, availability, and various other information. We reserve
            the right to correct any errors, inaccuracies, or omissions and to change or update the
            information on the Services at any time, without prior notice.
          </p>
        </section>

        <section id="disclaimer" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Disclaimer</h2>
          <p className={paragraphClasses}>
            The Services are provided on an as-is and as-available basis. You agree that your use of the
            Services will be at your sole risk. To the fullest extent permitted by law, we disclaim all
            warranties, express or implied, in connection with the Services and your use thereof, including,
            without limitation, the implied warranties of merchantability, fitness for a particular purpose,
            and non-infringement.
          </p>
          <p className={paragraphClasses}>
            We make no warranties or representations about the accuracy or completeness of the Services\'
            content or the content of any websites or mobile applications linked to the Services and we will
            assume no liability or responsibility for errors, personal injury or property damage, unauthorized
            access to our secure servers, interruption of transmission, bugs or viruses transmitted by third
            parties, or any errors or omissions in content and materials.
          </p>
        </section>

        <section id="limitations-of-liability" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Limitations of Liability</h2>
          <p className={paragraphClasses}>
            In no event will we or our directors, employees, or agents be liable to you or any third party for
            any direct, indirect, consequential, exemplary, incidental, special, or punitive damages,
            including lost profit, lost revenue, loss of data, or other damages arising from your use of the
            Services, even if we have been advised of the possibility of such damages.
          </p>
          <p className={paragraphClasses}>
            Notwithstanding anything to the contrary contained herein, our liability to you for any cause
            whatsoever and regardless of the form of the action will at all times be limited to the amount
            paid, if any, by you to us during the six-month period prior to any cause of action arising.
            Certain US state laws and international laws do not allow limitations on implied warranties or the
            exclusion or limitation of certain damages. If these laws apply to you, some or all of the above
            disclaimers or limitations may not apply to you, and you may have additional rights.
          </p>
        </section>

        <section id="indemnification" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Indemnification</h2>
          <p className={paragraphClasses}>
            You agree to defend, indemnify, and hold us harmless, including our subsidiaries, affiliates, and
            all of our respective officers, agents, partners, and employees, from and against any loss,
            damage, liability, claim, or demand, including reasonable attorneys\' fees and expenses, made by
            any third party due to or arising out of your use of the Services, your breach of these Legal
            Terms, any breach of your representations and warranties, your violation of the rights of a third
            party, or any overt harmful act toward any other user of the Services with whom you connected via
            the Services.
          </p>
          <p className={paragraphClasses}>
            Notwithstanding the foregoing, we reserve the right, at your expense, to assume the exclusive
            defense and control of any matter for which you are required to indemnify us, and you agree to
            cooperate, at your expense, with our defense of such claims. We will use reasonable efforts to
            notify you of any such claim, action, or proceeding which is subject to this indemnification upon
            becoming aware of it.
          </p>
        </section>

        <section id="user-data" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>User Data</h2>
          <p className={paragraphClasses}>
            We will maintain certain data that you transmit to the Services for the purpose of managing the
            performance of the Services, as well as data relating to your use of the Services. Although we
            perform regular routine backups of data, you are solely responsible for all data that you transmit
            or that relates to any activity you have undertaken using the Services. You agree that we shall
            have no liability to you for any loss or corruption of any such data, and you hereby waive any
            right of action against us arising from any such loss or corruption of such data.
          </p>
        </section>

        <section id="electronic-communications" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Electronic Communications, Transactions, and Signatures</h2>
          <p className={paragraphClasses}>
            Visiting the Services, sending us emails, and completing online forms constitute electronic
            communications. You consent to receive electronic communications, and you agree that all
            agreements, notices, disclosures, and other communications we provide to you electronically, via
            email and on the Services, satisfy any legal requirement that such communication be in writing.
          </p>
          <p className={paragraphClasses}>
            You hereby agree to the use of electronic signatures, contracts, orders, and other records, and to
            electronic delivery of notices, policies, and records of transactions initiated or completed by us
            or via the Services. You waive any rights or requirements under any statutes, regulations, rules,
            ordinances, or other laws in any jurisdiction which require an original signature or delivery or
            retention of non-electronic records, or to payments or the granting of credits by any means other
            than electronic means.
          </p>
        </section>

        <section id="california-users" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>California Users and Residents</h2>
          <p className={paragraphClasses}>
            If any complaint with us is not satisfactorily resolved, you can contact the Complaint Assistance
            Unit of the Division of Consumer Services of the California Department of Consumer Affairs in
            writing at 1625 North Market Blvd., Suite N 112, Sacramento, California 95834 or by telephone at
            (800) 952-5210 or (916) 445-1254.
          </p>
        </section>

        <section id="miscellaneous" className="mb-10 scroll-mt-24">
          <h2 className={heading2Classes}>Miscellaneous</h2>
          <p className={paragraphClasses}>
            These Legal Terms and any policies or operating rules posted by us on the Services or in respect to
            the Services constitute the entire agreement and understanding between you and us. Our failure to
            exercise or enforce any right or provision of these Legal Terms shall not operate as a waiver of
            such right or provision. These Legal Terms operate to the fullest extent permissible by law. We may
            assign any or all of our rights and obligations to others at any time. We shall not be responsible
            or liable for any loss, damage, delay, or failure to act caused by any cause beyond our reasonable
            control.
          </p>
          <p className={paragraphClasses}>
            If any provision or part of a provision of these Legal Terms is determined to be unlawful, void, or
            unenforceable, that provision or part of the provision is deemed severable from these Legal Terms
            and does not affect the validity and enforceability of any remaining provisions. There is no joint
            venture, partnership, employment, or agency relationship created between you and us as a result of
            these Legal Terms or use of the Services. You agree that these Legal Terms will not be construed
            against us by virtue of having drafted them. You waive any and all defenses you may have based on
            the electronic form of these Legal Terms and the lack of signing by the parties hereto to execute
            these Legal Terms.
          </p>
        </section>

        <section id="contact-us" className="scroll-mt-24">
          <h2 className={heading2Classes}>Contact Us</h2>
          <p className={paragraphClasses}>
            In order to resolve a complaint regarding the Services or to receive further information regarding
            use of the Services, please contact us at:
          </p>
          <p className={paragraphClasses}>
            SIX3RO, Inc.
            <br />
            11350 Random Hills Road, Suite 841
            <br />
            Fairfax, VA 22030
            <br />
            United States
            <br />
            Phone: +1 (703) 721-8202
            <br />
            Email: info@six3ro.com
          </p>
        </section>
      </div>

      <div className="mx-auto w-full max-w-4xl pt-8">
        <AuthFooter />
      </div>
    </main>
  </div>
);

export default TermsOfServicePage;
